/**
 * Pure math for the "Prognoza wpływów" (inflow forecast) tiles in the
 * KiedyNabory monthly settlements admin panel.
 *
 * Spec: docs/specifications/2026-09-10-prognoza-wplywow-proformy-churn/spec.md (AC-7/AC-8),
 * extended by docs/specifications/2026-10-21-prognoza-wykluczenie-przeterminowanych-proform/spec.md (AC-2).
 * All amounts are grosze (PLN cents), net.
 */

/** Result of validating a manual churn % input (AC-7). */
export type ManualChurnPctValidation =
  | { valid: true; value: number }
  | { valid: false; error: string };

/**
 * "Realnie wpłynie" (AC-8): (proformy + odnowienia) x (1 - churn), rounded to the
 * nearest grosz (never truncated).
 *
 * @param grossAmount - proformas total + renewals total, grosze, net
 * @param churnPct - effective churn percentage, 0-100
 */
export function calculateRealisticInflow(
  grossAmount: number,
  churnPct: number
): number {
  const factor = 1 - churnPct / 100;
  return Math.round(grossAmount * factor);
}

/**
 * Resolves the churn percentage actually used for the forecast math (AC-7):
 * a manual per-month override always wins; otherwise the historical rate;
 * "brak danych" (historicalPct === null) is treated as 0% until corrected.
 */
export function resolveEffectiveChurnPct(
  historicalPct: number | null,
  manualPct: number | null
): number {
  if (manualPct !== null) {
    return manualPct;
  }
  if (historicalPct === null) {
    return 0;
  }
  return historicalPct;
}

/**
 * Validates a manual churn % correction (AC-7): must be a number in [0, 100];
 * more than one decimal place is rounded to a 0.1 step (e.g. `12.34` -> `12.3`).
 * The caller keeps the previous effective value in effect when this returns invalid.
 */
export function validateManualChurnPct(
  rawInput: string
): ManualChurnPctValidation {
  const normalized = rawInput.trim().replace(",", ".");
  if (normalized === "") {
    return { valid: false, error: "Podaj wartość procentową." };
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return { valid: false, error: "Wpisz liczbę." };
  }
  if (parsed < 0 || parsed > 100) {
    return { valid: false, error: "Wartość musi być z zakresu 0–100." };
  }

  const rounded = clampChurnPct(Math.round(parsed * 10) / 10);
  return { valid: true, value: rounded };
}

/** Open-proforma aggregates feeding the forecast (grosze net / counts). */
export interface ForecastProformaInput {
  total: number;
  count: number;
  /** Optional so a payload missing the overdue fields degrades to "nothing overdue". */
  overdueTotal?: number;
  overdueCount?: number;
}

export interface ForecastInflowInput {
  proformas: ForecastProformaInput;
  renewalsTotal: number;
  historicalPct: number | null;
  manualPct: number | null;
}

export interface ForecastInflowResult {
  /** Manual % when set, else historical (null -> 0), clamped to [0, 100]. */
  baseChurnPct: number;
  /** Churn applied to proformas: the base, or the overdue share when that is higher. */
  effectiveProformaChurnPct: number;
  /** True when the overdue share of open proformas strictly exceeds the base churn. */
  isRaised: boolean;
  /** Overdue share (by count) of open proformas, 0.1 step; 0 when there are none. */
  overdueSharePct: number;
  /** Not-overdue proforma total — the ceiling on `proformaInflow`. */
  proformaCap: number;
  proformaInflow: number;
  renewalInflow: number;
  /** "Realnie wpłynie" = proformaInflow + renewalInflow. */
  realisticInflow: number;
  /** True when the not-overdue ceiling lowered `proformaInflow` below the churn-based value. */
  capBinds: boolean;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** Clamps a churn percentage to [0, 100]. */
export const clampChurnPct = (pct: number): number => clamp(pct, 0, 100);

/**
 * "Realnie wpłynie" with overdue proformas excluded (2026-10-21 AC-2). Integer
 * rule, no float comparisons:
 * - the overdue share (by count) of open proformas raises proforma churn above the
 *   base when it strictly exceeds it (a tie is not a raise);
 * - proforma inflow is `round(total x (1 - churn))`, capped at `total - overdueTotal`
 *   (never counts on overdue money);
 * - renewals keep the base churn (`calculateRealisticInflow`).
 */
export function calculateForecastInflow(
  input: ForecastInflowInput
): ForecastInflowResult {
  const { proformas, renewalsTotal, historicalPct, manualPct } = input;
  const baseChurnPct = clampChurnPct(
    resolveEffectiveChurnPct(historicalPct, manualPct)
  );
  const count = Math.max(0, proformas.count);
  const total = Math.max(0, proformas.total);
  const overdueCount = clamp(proformas.overdueCount ?? 0, 0, count);
  const overdueTotal = clamp(proformas.overdueTotal ?? 0, 0, total);

  // WHY integers: churn % has a 0.1 step, so both sides are scaled to tenths of a
  // percent (share x 1000 vs pct x 10). Comparing floats (e.g. 1/3 vs 33.3) would make
  // ties flip on rounding noise; this comparison is exact.
  const isRaised = overdueCount * 1000 > count * Math.round(baseChurnPct * 10);
  const overdueSharePct =
    count > 0 ? Math.round((overdueCount / count) * 1000) / 10 : 0;
  const effectiveProformaChurnPct = isRaised ? overdueSharePct : baseChurnPct;

  // Raised: exact fraction overdueCount/count (not the rounded display %).
  const uncappedInflow = isRaised
    ? Math.round((total * (count - overdueCount)) / count)
    : calculateRealisticInflow(total, baseChurnPct);
  const cap = total - overdueTotal;
  const proformaInflow = Math.max(0, Math.min(uncappedInflow, cap));
  const renewalInflow = calculateRealisticInflow(renewalsTotal, baseChurnPct);

  return {
    baseChurnPct,
    effectiveProformaChurnPct,
    isRaised,
    overdueSharePct,
    proformaCap: cap,
    proformaInflow,
    renewalInflow,
    realisticInflow: proformaInflow + renewalInflow,
    capBinds: cap < uncappedInflow,
  };
}

export interface ChurnStateInput {
  manualPct: number | null;
  historicalPct: number | null;
  basisCount: number;
  proformaCount: number;
  overdueCount: number;
  inflow: Pick<
    ForecastInflowResult,
    "isRaised" | "baseChurnPct" | "effectiveProformaChurnPct"
  >;
  monthEnded: boolean;
}

export interface ChurnState {
  /** Source of the base churn, as shown in "Bazowy (...)". */
  baseSourceLabel: string;
  /** The tile's main value. */
  valueLabel: string;
  /** Caption lines under the value (empty when there is nothing to say). */
  stateLines: string[];
  /** Warning variant (no data, not an ended month). */
  isWarning: boolean;
}

const formatPct = (pct: number): string =>
  `${pct.toLocaleString("pl-PL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

/** "jedyna otwarta proforma" / "wszystkie 3 otwarte" / "wszystkie 5 otwartych" + " po terminie". */
export const describeAllOverdue = (count: number): string => {
  if (count === 1) return "jedyna otwarta proforma po terminie";
  const mod10 = count % 10;
  const mod100 = count % 100;
  const few = mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14);
  return `wszystkie ${count} ${few ? "otwarte" : "otwartych"} po terminie`;
};

/** "1 otwarta proforma" / "3 otwarte proformy" / "5 otwartych proform". */
export const describeOpenProformas = (count: number): string => {
  if (count === 1) return "1 otwarta proforma";
  const mod10 = count % 10;
  const mod100 = count % 100;
  const few = mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14);
  return few ? `${count} otwarte proformy` : `${count} otwartych proform`;
};

/**
 * Text state of the "Churn proform" tile (2026-10-21 AC-3/AC-6): value, caption
 * lines and the warning flag. Pure, so the state matrix is unit-tested without
 * rendering.
 */
export function describeChurnState(input: ChurnStateInput): ChurnState {
  const {
    manualPct,
    historicalPct,
    basisCount,
    proformaCount,
    overdueCount,
    inflow,
    monthEnded,
  } = input;
  const { isRaised } = inflow;
  const baseSourceLabel =
    manualPct !== null
      ? "ręczny"
      : historicalPct !== null
        ? "historyczny"
        : "brak danych historycznych";
  const baseLabel =
    manualPct !== null || historicalPct !== null
      ? `${baseSourceLabel} ${formatPct(inflow.baseChurnPct)}`
      : baseSourceLabel;
  const overdueLabel = `${overdueCount} z ${proformaCount} po terminie`;

  const valueLabel = isRaised
    ? formatPct(inflow.effectiveProformaChurnPct)
    : manualPct !== null
      ? formatPct(manualPct)
      : historicalPct !== null
        ? formatPct(historicalPct)
        : "brak danych";

  let stateLines: string[] = [];
  if (isRaised && monthEnded) {
    stateLines = [`miesiąc zakończony — stan informacyjny (${overdueLabel})`];
  } else if (isRaised && overdueCount === proformaCount) {
    stateLines = [describeAllOverdue(proformaCount)];
  } else if (isRaised) {
    stateLines = [baseLabel, `→ podniesiony (${overdueLabel})`];
  } else if (manualPct !== null) {
    stateLines = ["ręczny"];
  } else if (historicalPct !== null) {
    stateLines = [`historyczny (12 mies., n=${basisCount})`];
  }

  // An ended month is informational only — never highlighted as a warning.
  const isWarning =
    manualPct === null && historicalPct === null && !(isRaised && monthEnded);

  return { baseSourceLabel, valueLabel, stateLines, isWarning };
}
